// TrainingSubmitForm
// Renders the "Curated Training Set" form card including:
//   - Entity type + entity selector
//   - Question textarea
//   - SQL generate button, SQL textarea, validate button + result
//   - Override duplicate, entity mapping confirmed, SQL validated status, 100% correct checkboxes
//   - Duplicate warning block
//   - Submit button + progress count
// All state and handlers are owned by ConsoleTraining.tsx and passed in as props.

import React, { FormEvent } from 'react';
import { DashboardSqlValidationResult, TrainingEntityType } from '../../../types';
import { EntityOption } from './types';

interface TrainingSubmitFormProps {
  // Entity selection
  entityType: TrainingEntityType;
  setEntityType: (v: TrainingEntityType) => void;
  selectedEntityId: string;
  setSelectedTableEntityId: (v: string) => void;
  setSelectedDashboardEntityId: (v: string) => void;
  entityOptions: EntityOption[];

  // Question
  question: string;
  setQuestion: (v: string) => void;

  // SQL
  sqlText: string;
  setSqlText: (v: string) => void;
  setSqlSource: (v: 'manual' | 'generated') => void;
  setSqlValidated: (v: boolean) => void;
  setSqlValidationResult: (v: DashboardSqlValidationResult | null) => void;
  sqlSource: 'manual' | 'generated';
  sqlValidated: boolean;
  sqlValidationResult: DashboardSqlValidationResult | null;
  generatingSql: boolean;
  generatingQuestion: boolean;
  validatingSql: boolean;
  handleGenerateSql: () => Promise<void>;
  handleGenerateQuestion: () => Promise<void>;
  handleValidateSql: () => Promise<void>;

  // Checkboxes
  overrideDuplicate: boolean;
  setOverrideDuplicate: (v: boolean) => void;
  entityMappingConfirmed: boolean;
  setEntityMappingConfirmed: (v: boolean) => void;
  is100Correct: boolean;
  setIs100Correct: (v: boolean) => void;

  // Duplicate warning
  duplicateMatch: string;
  duplicateScore: number | null;

  // Submit
  submitting: boolean;
  myProgressCount: number;
  handleSubmitQuestion: (event: FormEvent) => Promise<void>;
}

const TrainingSubmitForm: React.FC<TrainingSubmitFormProps> = ({
  entityType,
  setEntityType,
  selectedEntityId,
  setSelectedTableEntityId,
  setSelectedDashboardEntityId,
  entityOptions,
  question,
  setQuestion,
  sqlText,
  setSqlText,
  setSqlSource,
  setSqlValidated,
  setSqlValidationResult,
  sqlSource,
  sqlValidated,
  sqlValidationResult,
  generatingSql,
  generatingQuestion,
  validatingSql,
  handleGenerateSql,
  handleGenerateQuestion,
  handleValidateSql,
  overrideDuplicate,
  setOverrideDuplicate,
  entityMappingConfirmed,
  setEntityMappingConfirmed,
  is100Correct,
  setIs100Correct,
  duplicateMatch,
  duplicateScore,
  submitting,
  myProgressCount,
  handleSubmitQuestion,
}) => {
  return (
    <div className="console-card">
      <h2>Curated Training Set</h2>
      <p>Each assigned developer submits 30 unique questions per table or dashboard.</p>

      <form className="console-form" onSubmit={handleSubmitQuestion}>

        {/* ── Entity Type ── */}
        <label>
          Entity Type
          <select value={entityType} onChange={(e) => setEntityType(e.target.value as TrainingEntityType)}>
            <option value="table">Table</option>
            <option value="dashboard">Dashboard</option>
          </select>
        </label>

        {/* ── Entity Selector ── */}
        <label>
          {entityType === 'table' ? 'Table' : 'Dashboard'}
          <select
            value={selectedEntityId}
            onChange={(e) => {
              const nextId = e.target.value;
              if (entityType === 'table') {
                setSelectedTableEntityId(nextId);
              } else {
                setSelectedDashboardEntityId(nextId);
              }
            }}
            required
          >
            <option value="">Select {entityType}</option>
            {entityOptions.map((item) => (
              <option key={`${entityType}-${item.id}`} value={item.id}>
                {item.label}
              </option>
            ))}
          </select>
        </label>

        {/* ── Question ── */}
        <label>
          Question
          <textarea
            rows={5}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Enter a table-specific business question"
            required
          />
        </label>

        {/* ── SQL Generate + Textarea ── */}
        <div className="inline-header">
          <h3>SQL</h3>
          <button type="button" onClick={handleGenerateQuestion} disabled={generatingQuestion || !selectedEntityId}>
            {generatingQuestion ? 'Generating...' : 'Generate Questions'}
          </button>
          <button type="button" onClick={handleGenerateSql} disabled={generatingSql || !question.trim()}>
            {generatingSql ? 'Generating...' : 'Generate SQL'}
          </button>
        </div>

        <label>
          SQL Text
          <textarea
            rows={6}
            value={sqlText}
            onChange={(e) => {
              setSqlText(e.target.value);
              setSqlSource('manual');
              setSqlValidated(false);
              setSqlValidationResult(null);
            }}
            placeholder="Paste SQL or generate SQL first"
          />
        </label>

        {/* ── SQL Validate ── */}
        <div className="inline-header">
          <p>SQL source: <strong>{sqlSource}</strong></p>
          <button type="button" onClick={handleValidateSql} disabled={validatingSql || !sqlText.trim()}>
            {validatingSql ? 'Validating...' : 'Validate SQL'}
          </button>
        </div>

        {sqlValidationResult ? (
          <div className="console-subcard">
            <p>
              <strong>Total Rows:</strong> {sqlValidationResult.total_rows} | <strong>Execution:</strong>{' '}
              {sqlValidationResult.execution_ms} ms
            </p>
          </div>
        ) : null}

        {/* ── Checkboxes ── */}
        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={overrideDuplicate}
            onChange={(e) => setOverrideDuplicate(e.target.checked)}
          />
          Override duplicate check if needed
        </label>

        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={entityMappingConfirmed}
            onChange={(e) => setEntityMappingConfirmed(e.target.checked)}
          />
          I confirm this question maps to the selected entity
        </label>

        <div className="console-subcard">
          <strong>SQL validation status:</strong> {sqlValidated ? 'Validated' : 'Not validated'}
        </div>

        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={is100Correct}
            onChange={(e) => setIs100Correct(e.target.checked)}
          />
          This question is 100% correct
        </label>

        {/* ── Duplicate Warning ── */}
        {duplicateMatch ? (
          <div className="console-warning">
            <p>Similar question detected ({duplicateScore ?? 0}).</p>
            <p>{duplicateMatch}</p>
          </div>
        ) : null}

        <button type="submit" disabled={submitting || myProgressCount >= 30}>
          {submitting ? 'Submitting...' : 'Submit Question'}
        </button>
      </form>

      <p>
        Your progress: <strong>{myProgressCount}/30</strong>
      </p>
    </div>
  );
};

export default TrainingSubmitForm;